import { Address } from '@ton/core';
import { DEFAULT_CURVE_CONFIG, simulateLaunch, toNano, parseStartParam } from '@gong/curve-math';
import { TonConnectButton, useTonAddress, useTonConnectUI, useTonWallet } from '@tonconnect/ui-react';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { API_URL, APP_VERSION, BOT_USERNAME, FACTORY_ADDRESS, HAS_FACTORY, IS_TESTNET, QUOTE_TTL_SECONDS } from './config';
import { Copy, detectLang, t } from './i18n';
import { unixNow } from './lib/format';
import { loadClaimableFees, loadFactory, loadFactoryParams, loadJettonWallet, loadLaunch, loadLockerReadiness, parseFriendlyAddress } from './chain/ton';
import { loadStonDexParams } from './chain/ston';
import { assembleBuy, assembleClaim, assembleCreateLaunch, assembleHarvest, assembleSell, newLaunchSalt } from './quotes/assemble';
import { quoteBuy, quoteSell } from './quotes/quote';
import { HomeScreen } from './screens/HomeScreen';
import { DiscoveryScreen } from './screens/DiscoveryScreen';
import { PendingLaunch, rememberPendingLaunch } from './screens/PendingLaunch';
import { api, IndexedToken } from './lib/api';
import { LaunchScreen, LaunchSubmit } from './screens/LaunchScreen';
import { TokenModel, TokenScreen } from './screens/TokenScreen';
import { NotificationSettingsScreen } from './screens/NotificationSettingsScreen';
import { AdminScreen } from './screens/AdminScreen';
import { useTelegramSession } from './state/useTelegramSession';
import {
  haptic, offerHomeScreen, persist, readPersisted, requestTelegramChat,
  requestWriteAccess, sharePreparedMessage, webApp,
} from './telegram/webapp';

type Route = { name: 'home' } | { name: 'launchpad' } | { name: 'launch' } | { name: 'settings' } | { name: 'admin' } | { name: 'token'; id: string };
type PreparedLaunch = {
  id: string;
  metadataUri: string;
  imageKey: string | null;
  walletAddress: string;
  name: string;
  ticker: string;
  salt: bigint;
};

const RECENT_KEY = 'gong.recent.curves';
const DEMO_ID = 'demo';

function parseRoute(hash: string): Route {
  const path = hash.replace(/^#/, '') || '/';
  if (path === '/launchpad') return { name: 'launchpad' };
  if (path === '/admin') return { name: 'admin' };
  if (path.startsWith('/launch')) {
    return { name: 'launch' };
  }
  if (path.startsWith('/settings')) return { name: 'settings' };
  const token = path.match(/^\/t\/([^/]+)/);
  if (token?.[1]) {
    return { name: 'token', id: decodeURIComponent(token[1]) };
  }
  return { name: 'home' };
}

function go(route: Route): void {
  if (route.name === 'home') {
    window.location.hash = '/';
  } else if (route.name === 'launchpad') {
    window.location.hash = '/launchpad';
  } else if (route.name === 'launch') {
    window.location.hash = '/launch';
  } else if (route.name === 'settings') {
    window.location.hash = '/settings';
  } else if (route.name === 'admin') {
    window.location.hash = '/admin';
  } else {
    window.location.hash = `/t/${encodeURIComponent(route.id)}`;
  }
}

function makeDemo(now = unixNow()): TokenModel {
  const seedTon = toNano('50');
  const devBuyTon = toNano('2');
  const result = simulateLaunch(DEFAULT_CURVE_CONFIG, { seedTon, devBuyTon, now });
  return {
    id: DEMO_ID,
    demo: true,
    name: 'DEMO',
    ticker: 'DEMO',
    state: result.state,
    config: DEFAULT_CURVE_CONFIG,
    seedTon,
    devBuyTon,
  };
}

function buildCopy(lang: ReturnType<typeof detectLang>): Copy {
  return new Proxy(
    {},
    {
      get: (_target, prop: string) => t(lang, prop as keyof Copy),
    },
  ) as Copy;
}

export function App() {
  const lang = detectLang(webApp()?.initDataUnsafe.user?.language_code);
  const copy = useMemo(() => buildCopy(lang), [lang]);
  const session = useTelegramSession(lang);
  const wallet = useTonWallet();
  const address = useTonAddress();
  const [tonConnectUI] = useTonConnectUI();
  const [route, setRoute] = useState<Route>(() => parseRoute(window.location.hash));
  const [token, setToken] = useState<TokenModel | null>(null);
  const [recent, setRecent] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [launchFee, setLaunchFee] = useState(DEFAULT_CURVE_CONFIG.launchFee);
  const [launchConfig, setLaunchConfig] = useState(DEFAULT_CURVE_CONFIG);
  const [factoryReady, setFactoryReady] = useState(false);
  const [factoryError, setFactoryError] = useState<string | null>(null);
  const [paused, setPaused] = useState(false);
  const [loadingToken, setLoadingToken] = useState(false);
  const [walletVerified, setWalletVerified] = useState(false);
  const [refreshToken, setRefreshToken] = useState(0);
  const [buyAmount, setBuyAmount] = useState<string | undefined>();
  const [launchStep, setLaunchStep] = useState<string | null>(null);
  const [preparedLaunch, setPreparedLaunch] = useState<PreparedLaunch | null>(null);
  const [adminAllowed, setAdminAllowed] = useState(false);

  useEffect(() => {
    if (!session.token) { setAdminAllowed(false); return; }
    let cancelled = false;
    void api('/api/admin', session.token).then(() => { if (!cancelled) setAdminAllowed(true); })
      .catch(() => { if (!cancelled) setAdminAllowed(false); });
    return () => { cancelled = true; };
  }, [session.token]);

  const verifyConnectedWallet = useCallback(async (): Promise<boolean> => {
    if (!session.token || !wallet) return false;
    const typed = wallet as unknown as {
      account: { address: string; publicKey?: string };
      connectItems?: { tonProof?: { proof?: {
        timestamp: number; domain: { value: string }; signature: string; payload: string;
      } } };
    };
    const proof = typed.connectItems?.tonProof?.proof;
    if (!proof) return false;
    try {
      await apiCall('/api/ton-proof/verify', session.token, {
        address: typed.account.address,
        publicKey: typed.account.publicKey ?? '',
        signature: proof.signature,
        timestamp: proof.timestamp,
        domain: proof.domain.value,
        payload: proof.payload,
      });
      setWalletVerified(true);
      return true;
    } catch {
      setWalletVerified(false);
      return false;
    }
  }, [session.token, wallet]);

  useEffect(() => {
    const ui = tonConnectUI as unknown as {
      setConnectRequestParameters?: (value: unknown) => void;
    };
    if (!session.token) {
      ui.setConnectRequestParameters?.(null);
      return;
    }
    ui.setConnectRequestParameters?.({ state: 'loading' });
    void apiCall<{ payload: string }>('/api/ton-proof/nonce', session.token, {})
      .then(({ payload }) => ui.setConnectRequestParameters?.({ state: 'ready', value: { tonProof: payload } }))
      .catch(() => ui.setConnectRequestParameters?.(null));
  }, [session.token, tonConnectUI]);

  useEffect(() => {
    setWalletVerified(false);
    void verifyConnectedWallet();
  }, [verifyConnectedWallet]);

  useEffect(() => {
    const onHash = () => setRoute(parseRoute(window.location.hash));
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, []);

  useEffect(() => {
    void readPersisted(RECENT_KEY).then((raw) => {
      if (!raw) {
        return;
      }
      try {
        setRecent(JSON.parse(raw) as string[]);
      } catch {
        setRecent([]);
      }
    });
  }, []);

  useEffect(() => {
    const start = webApp()?.initDataUnsafe.start_param;
    if (!start) return;
    const parsed = parseStartParam(start);
    if (!parsed) return;
    go({ name: 'token', id: parsed.curve });
    setBuyAmount(parsed.amount);
    if (session.token && parsed.amount) void apiCall('/api/purchase-intents', session.token, {
      curve: parsed.curve, amount: toNano(parsed.amount).toString(), side: 'buy', surface: parsed.surface ?? 'mini_app',
    }).catch(() => undefined);
  }, [session.token]);

  useEffect(() => {
    if (!HAS_FACTORY) {
      return;
    }
    void Promise.all([loadFactory(Address.parse(FACTORY_ADDRESS)), loadFactoryParams(Address.parse(FACTORY_ADDRESS))])
      .then(([snap, params]) => {
        setLaunchFee(snap.launchFee);
        setPaused(snap.paused);
        setLaunchConfig({ ...params.config, launchFee: snap.launchFee });
        setFactoryReady(true);
      })
      .catch(() => setFactoryError('Не удалось прочитать параметры фабрики. Повторите открытие приложения.'));
  }, []);

  const remember = useCallback((curve: string) => {
    setRecent((prev) => {
      const next = [curve, ...prev.filter((item) => item !== curve)].slice(0, 8);
      persist(RECENT_KEY, JSON.stringify(next));
      return next;
    });
  }, []);

  useEffect(() => {
    if (route.name !== 'token') {
      return;
    }
    setError(null);
    if (route.id === DEMO_ID) {
      setToken(makeDemo());
      return;
    }
    setLoadingToken(true);
    let cancelled = false;
    const curve = parseFriendlyAddress(route.id);
    void Promise.all([loadLaunch(curve), api<IndexedToken>(`/api/tokens/${encodeURIComponent(curve.toRawString())}`).catch(() => null)])
      .then(([loaded, indexed]) => {
        if (cancelled) return;
        setToken({
          id: route.id,
          demo: false,
          name: indexed?.name ?? 'Токен',
          ticker: indexed?.ticker ?? loaded.minter?.toRawString().slice(-6) ?? 'TOKEN',
          imageUrl: indexed?.imageUrl,
          passport: indexed?.passport,
          curve: loaded.curve,
          minter: loaded.minter,
          state: loaded.state,
          config: loaded.config,
          seedTon: loaded.state.seedTon + loaded.state.excessSeedTon,
          devBuyTon: BigInt(indexed?.dev_buy_ton ?? '0'),
          deployer: loaded.deployer,
          channel: loaded.channel,
          feeVault: loaded.feeVault,
          locker: loaded.locker,
          feeEarnings: indexed?.feeEarnings,
        });
        remember(
          loaded.curve.toString({ bounceable: true, urlSafe: true, testOnly: IS_TESTNET }),
        );
      })
      .catch(() => {
        if (cancelled) return;
        setToken(null);
        setError(copy.loadFailed);
      })
      .finally(() => { if (!cancelled) setLoadingToken(false); });
    return () => { cancelled = true; };
  }, [route, remember, copy.loadFailed, refreshToken]);

  async function sendTx(tx: { validUntil: number; messages: { address: string; amount: string; payload: string }[] }): Promise<boolean> {
    if (!wallet) {
      setError(copy.connectFirst);
      return false;
    }
    if (tx.validUntil <= unixNow()) {
      setError(copy.quoteExpired);
      return false;
    }
    setBusy(true);
    setError(null);
    try {
      haptic('light');
      await tonConnectUI.sendTransaction(tx);
      haptic('success');
      setNotice(copy.sent);
      window.setTimeout(() => setRefreshToken((v) => v + 1), 12000);
      void requestWriteAccess().then((granted) => {
        if (!granted || !session.token) return;
        return fetch(`${API_URL.replace(/\/$/, '')}/api/notification-preferences`, {
          method: 'PUT',
          headers: { authorization: `Bearer ${session.token}`, 'content-type': 'application/json' },
          body: JSON.stringify({ canWrite: true }),
        });
      }).catch(() => undefined);
      offerHomeScreen();
      return true;
    } catch (err) {
      haptic('error');
      setError(err instanceof Error ? err.message : copy.loadFailed);
      return false;
    } finally {
      setBusy(false);
    }
  }

  async function onLaunch(draft: LaunchSubmit) {
    if (!HAS_FACTORY) {
      setError(copy.noFactory);
      return;
    }
    if (!factoryReady) {
      setError(factoryError ?? 'Дождитесь загрузки параметров фабрики.');
      return;
    }
    if (!session.token) {
      setError(copy.openInTelegram);
      return;
    }
    if (!address) {
      setError(copy.connectFirst);
      return;
    }
    if (!walletVerified && !(await verifyConnectedWallet())) {
      setError(copy.walletProofNeeded);
      return;
    }
    const connected = Address.parse(address);
    setBusy(true);
    setError(null);
    setNotice(null);
    setLaunchStep(preparedLaunch ? 'Готовим транзакцию…' : 'Готовим выбор канала…');
    try {
      const nativeChat = Boolean(webApp()?.isVersionAtLeast('9.6') && webApp()?.requestChat);
      if (!preparedLaunch) {
        const created = await apiCall<{ id: string; metadataUri: string | null }>(
          '/api/launch-drafts', session.token, {
            name: draft.name, ticker: draft.ticker, channelRecipient: connected.toRawString(),
          },
        );
        if (!created.metadataUri) throw new Error('Хранилище метаданных не настроено.');
        setLaunchStep('Выберите канал в Telegram');
        const channel = await apiCall<{ preparedButtonId: string }>(
          `/api/launch-drafts/${created.id}/prepare-channel${nativeChat ? '' : '?fallback=1'}`,
          session.token, {},
        );
        if (!nativeChat || !(await requestTelegramChat(channel.preparedButtonId))) {
          if (nativeChat) await apiCall(`/api/launch-drafts/${created.id}/prepare-channel?fallback=1`, session.token, {});
          setNotice('Выберите канал в личном чате с ботом, затем вернитесь в открытое приложение.');
          webApp()?.openTelegramLink?.(`https://t.me/${BOT_USERNAME}`);
        }
        const boundDraft = await waitForChat(created.id, 'channel', session.token);
        setPreparedLaunch({
          id: created.id, metadataUri: created.metadataUri, imageKey: boundDraft.image_key,
          walletAddress: connected.toRawString(), name: draft.name, ticker: draft.ticker,
          salt: newLaunchSalt(),
        });
        setNotice(boundDraft.image_key
          ? 'Канал выбран. Проверьте его аватар ниже: его можно заменить до подписания.'
          : 'Канал выбран, но аватар недоступен. Загрузите картинку токена перед подписанием.');
        return;
      }
      if (connected.toRawString() !== preparedLaunch.walletAddress) {
        throw new Error('Подключите тот же кошелёк, с которым выбирали канал.');
      }
      if (draft.imageBase64) {
        await apiCall(`/api/launch-drafts/${preparedLaunch.id}/image`, session.token, {
          imageBase64: draft.imageBase64, imageMime: draft.imageMime,
        });
      } else if (!preparedLaunch.imageKey) {
        throw new Error('Выберите картинку токена перед подписанием.');
      }
      setLaunchStep('Выберите чат токена (можно пропустить)');
      const group = nativeChat ? await apiCall<{ preparedButtonId: string }>(
        `/api/launch-drafts/${preparedLaunch.id}/prepare-group`, session.token, {},
      ) : null;
      if (group && await requestTelegramChat(group.preparedButtonId)) {
        await waitForChat(preparedLaunch.id, 'group', session.token);
      }
      await apiCall(`/api/launch-drafts/${preparedLaunch.id}/submit`, session.token, {
        salt: preparedLaunch.salt.toString(), channelRecipient: connected.toRawString(),
      });
      setLaunchStep('Проверяем адреса STON.fi');
      const stonDexParams = IS_TESTNET
        ? await loadStonDexParams(Address.parse(FACTORY_ADDRESS), connected, preparedLaunch.salt, preparedLaunch.metadataUri)
        : undefined;
      setLaunchStep('Подтвердите запуск в кошельке');
      const sent = await sendTx(
        assembleCreateLaunch({
          factory: Address.parse(FACTORY_ADDRESS),
          salt: preparedLaunch.salt,
          contentUri: preparedLaunch.metadataUri,
          channel: connected,
          seedTon: draft.seed,
          devBuyTon: draft.devBuy,
          deadline: unixNow() + QUOTE_TTL_SECONDS,
          launchFee,
          stonDexParams,
        }),
      );
      if (!sent) return;
      if (session.user) rememberPendingLaunch(session.user.id, preparedLaunch.id);
      setPreparedLaunch(null);
      setBusy(true);
      setNotice(copy.confirmingLaunch);
      setLaunchStep('Подтверждаем запуск в сети…');
      const curve = await waitForLaunchedToken(preparedLaunch.id, session.token);
      remember(curve);
      setNotice(copy.launchConfirmed);
      go({ name: 'token', id: curve });
    } catch (err) {
      if (err instanceof LaunchConfirmationTimeout) {
        setNotice(copy.launchConfirmationDelayed);
      } else {
        setError(err instanceof Error ? err.message : copy.loadFailed);
      }
    } finally {
      setBusy(false);
      setLaunchStep(null);
    }
  }

  async function onBuy(tonIn: bigint) {
    if (!token) {
      return;
    }
    const quoted = quoteBuy(token.state, tonIn, { now: unixNow(), config: token.config });
    if (token.demo) {
      setToken({ ...token, state: quoted.state });
      haptic('success');
      setNotice(copy.sent);
      return;
    }
    if (!token.curve) {
      return;
    }
    await sendTx(
      assembleBuy({
        curve: token.curve,
        tonIn,
        minOut: quoted.minOut,
        deadline: quoted.deadline,
      }),
    );
  }

  async function onSell(jettons: bigint) {
    if (!token) {
      return;
    }
    const quoted = quoteSell(token.state, jettons, { now: unixNow(), config: token.config });
    if (token.demo) {
      setToken({ ...token, state: quoted.state });
      haptic('success');
      setNotice(copy.sent);
      return;
    }
    if (!token.curve || !token.minter || !address) {
      setError(copy.needWalletForSell);
      return;
    }
    const owner = Address.parse(address);
    const jettonWallet = await loadJettonWallet(token.minter, owner);
    await sendTx(
      assembleSell({
        jettonWallet,
        curve: token.curve,
        responseDestination: owner,
        jettons,
        minOut: quoted.minOut,
        deadline: quoted.deadline,
      }),
    );
  }

  async function onClaim(): Promise<boolean> {
    if (!token?.feeVault || !token.channel || !address) {
      setError(copy.connectFirst);
      return false;
    }
    const connected = Address.parse(address);
    const fees = await loadClaimableFees(token.feeVault, token.channel, connected);
    if (!fees.recipient.equals(connected) || fees.claimable === 0n) {
      setError('Нет доступных комиссий для подключённого кошелька. Обновите данные.');
      return false;
    }
    return sendTx(assembleClaim(token.feeVault, unixNow() + 180));
  }

  async function onHarvest(): Promise<boolean> {
    if (!token?.curve || !token.locker || !address || token.state.phase !== 'Graduated') {
      setError('Сбор комиссий пула доступен после градуации токена и подключения кошелька.');
      return false;
    }
    const connected = Address.parse(address);
    if (!token.deployer?.equals(connected) && !token.channel?.equals(connected)) {
      setError('Подключите кошелёк создателя или получателя комиссий этого токена.');
      return false;
    }
    if (!(await loadLockerReadiness(token.locker, token.curve))) {
      setError('LP-локер ещё не готов к сбору комиссий. Обновите данные позже.');
      return false;
    }
    return sendTx(assembleHarvest(token.locker, unixNow() + 180));
  }

  async function onShare(curve: string) {
    if (!session.token) {
      webApp()?.switchInlineQuery?.(curve, ['users', 'groups', 'channels']);
      return;
    }
    try {
      const prepared = await apiCall<{ id: string; fallbackQuery: string }>(
        '/api/prepared-shares', session.token, { curve },
      );
      sharePreparedMessage(prepared.id, prepared.fallbackQuery);
    } catch {
      webApp()?.switchInlineQuery?.(curve, ['users', 'groups', 'channels']);
    }
  }

  const title =
    route.name === 'launch' ? copy.launchToken : route.name === 'settings' ? 'Уведомления' : route.name === 'admin' ? 'Админ' : route.name === 'token' ? (token?.ticker ?? copy.openToken) : copy.appName;

  const sessionBanner =
    session.status === 'outside'
      ? copy.openInTelegram
      : session.status === 'ok'
        ? copy.sessionOk
        : session.status === 'unverified'
          ? copy.sessionUnverified
          : session.status === 'fail'
            ? copy.sessionFail
            : null;

  return (
    <div className="app">
      <header className="topbar">
        {route.name !== 'home' && route.name !== 'launchpad' && route.name !== 'admin' ? (
          <button type="button" className="ghost" onClick={() => go({ name: 'home' })}>
            {copy.back}
          </button>
        ) : (
          <span className="brand-mark" aria-hidden="true">G</span>
        )}
        <div style={{ flex: 1, minWidth: 0 }}>
          <h1>
            {title}
            <div className="sub">TON <span className="testnet-dot" /> Testnet</div>
          </h1>
        </div>
        <div className="wallet">
          <TonConnectButton />
        </div>
      </header>

      {sessionBanner && session.status !== 'ok' && route.name === 'launchpad' ? (
        <div className="banner" style={{ margin: '12px 12px 0' }}>
          {sessionBanner}
          {session.user ? ` · ${session.user.first_name}` : ''}
        </div>
      ) : null}
      <PendingLaunch user={session.user?.id} session={session.token} onOpen={(id) => go({ name: 'token', id })} />
      {notice ? (
        <div className="banner ok" style={{ margin: '12px 12px 0' }}>
          {notice}
        </div>
      ) : null}
      {error && route.name === 'home' ? (
        <div className="banner warn" style={{ margin: '12px 12px 0' }}>
          {error}
        </div>
      ) : null}

      {route.name === 'home' ? (
        <DiscoveryScreen address={address} session={session.token} onOpen={(id) => { setBuyAmount(undefined); go({ name: 'token', id }); }} onLaunch={() => go({ name: 'launchpad' })} />
      ) : null}
      {route.name === 'launchpad' ? (
        <HomeScreen
          copy={copy}
          connected={Boolean(wallet)}
          recent={recent}
          onLaunch={() => go({ name: 'launch' })}
          onDemo={() => go({ name: 'token', id: DEMO_ID })}
          onSettings={() => go({ name: 'settings' })}
          onOpen={(value) => {
            try {
              const parsed = parseFriendlyAddress(value);
              go({
                name: 'token',
                id: parsed.toString({ bounceable: true, urlSafe: true, testOnly: IS_TESTNET }),
              });
            } catch {
              setError(copy.invalidAddress);
            }
          }}
        />
      ) : null}

      {route.name === 'launch' ? (
        <LaunchScreen
          copy={copy}
          config={launchConfig}
          ready={factoryReady}
          launchFee={launchFee}
          paused={paused}
          busy={busy}
          busyLabel={launchStep}
          error={factoryError ?? error}
          channelSelected={Boolean(preparedLaunch)}
          channelImageUrl={preparedLaunch?.imageKey
            ? `${window.location.origin}/${preparedLaunch.imageKey}` : null}
          lockedName={preparedLaunch?.name}
          lockedTicker={preparedLaunch?.ticker}
          onSubmit={(draft) => void onLaunch(draft)}
        />
      ) : null}

      {route.name === 'settings' ? <NotificationSettingsScreen token={session.token} /> : null}
      {route.name === 'admin' ? adminAllowed && session.token
        ? <AdminScreen session={session.token} walletAddress={address} sendTx={sendTx} />
        : <main className="scroll"><p className="hint">Раздел доступен только владельцу бота в Telegram.</p></main> : null}

      {route.name === 'token' ? (
        loadingToken ? (
          <p className="hint" style={{ padding: 16 }}>
            {copy.lookingUp}
          </p>
        ) : token ? (
          <TokenScreen
            key={token.id}
            copy={copy}
            token={token}
            busy={busy}
            error={error}
            onBuy={(value) => void onBuy(value).catch((e: unknown) => setError(e instanceof Error ? e.message : copy.loadFailed))}
            onSell={(value) => void onSell(value).catch((e: unknown) => setError(e instanceof Error ? e.message : copy.loadFailed))}
            onShare={() => void onShare(token.id)}
            initialBuyAmount={buyAmount}
            session={session.token}
            walletAddress={address}
            refreshVersion={refreshToken}
            onClaim={() => onClaim().catch((e: unknown) => {
              setError(e instanceof Error ? e.message : copy.loadFailed);
              return false;
            })}
            onHarvest={() => onHarvest().catch((e: unknown) => {
              setError(e instanceof Error ? e.message : copy.loadFailed);
              return false;
            })}
            onRefresh={() => setRefreshToken((v) => v + 1)}
          />
        ) : (
          <p className="hint" style={{ padding: 16 }}>
            {error ?? copy.loadFailed}
          </p>
        )
      ) : null}
      <footer className="app-footer">Версия {APP_VERSION}</footer>
      <nav className="bottom-nav" aria-label="Основная навигация">
        <button aria-current={route.name === 'home' || route.name === 'token' ? 'page' : undefined} onClick={() => go({ name: 'home' })}><svg viewBox="0 0 24 24" aria-hidden="true"><path d="m3 10 9-7 9 7v10a1 1 0 0 1-1 1h-5v-7H9v7H4a1 1 0 0 1-1-1Z" /></svg><span>Главная</span></button>
        <button aria-current={['launchpad', 'launch', 'settings'].includes(route.name) ? 'page' : undefined} onClick={() => go({ name: 'launchpad' })}><svg viewBox="0 0 24 24" aria-hidden="true"><path d="M14 4c2-1 4-1 6-1 0 2 0 4-1 6l-7 7-4-4Z M8 12l-4 1 1-5 7-3 M12 16l-1 4 5-1 3-7 M4 20l3-3" /><circle cx="16" cy="7" r="1" /></svg><span>Лаунчпад</span></button>
        {adminAllowed && <button aria-current={route.name === 'admin' ? 'page' : undefined} onClick={() => go({ name: 'admin' })}><svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 2 4 5v6c0 5 3 9 8 11 5-2 8-6 8-11V5l-8-3Z M9 12l2 2 4-4" /></svg><span>Админ</span></button>}
      </nav>
    </div>
  );
}

async function apiCall<T = unknown>(path: string, token: string, body?: unknown): Promise<T> {
  const response = await fetch(`${API_URL.replace(/\/$/, '')}${path}`, {
    method: body === undefined ? 'GET' : 'POST',
    headers: { authorization: `Bearer ${token}`, ...(body === undefined ? {} : { 'content-type': 'application/json' }) },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  const parsed = await response.json() as T & { error?: string };
  if (!response.ok) throw new Error(parsed.error ?? `API ${response.status}`);
  return parsed;
}

async function waitForChat(draftId: string, kind: 'channel' | 'group', token: string): Promise<{
  image_key: string | null;
}> {
  for (let attempt = 0; attempt < 300; attempt += 1) {
    const draft = await apiCall<{ image_key: string | null } & Record<'channel' | 'group', {
      chat_id: number | null; owner_verified: number; bot_rights_verified: number;
    } | null>>(
      `/api/launch-drafts/${draftId}`, token,
    );
    const chat = draft[kind];
    if (chat?.chat_id && chat.owner_verified === 1 && chat.bot_rights_verified === 1) return draft;
    await new Promise((resolve) => window.setTimeout(resolve, 1000));
  }
  throw new Error('Telegram не подтвердил права на канал или чат.');
}

class LaunchConfirmationTimeout extends Error {}

async function waitForLaunchedToken(draftId: string, token: string): Promise<string> {
  for (let attempt = 0; attempt < 60; attempt += 1) {
    const draft = await apiCall<{ curve_address: string | null }>(
      `/api/launch-drafts/${draftId}`, token,
    );
    if (draft.curve_address) {
      try {
        const launch = await apiCall<{ phase: string; minter_address: string | null }>(`/api/tokens/${encodeURIComponent(draft.curve_address)}`, token);
        if (launch.minter_address && launch.phase !== 'Initializing') return draft.curve_address;
      } catch {
        // The factory event and curve event can be indexed on adjacent cron runs.
      }
    }
    await new Promise((resolve) => window.setTimeout(resolve, 2000));
  }
  throw new LaunchConfirmationTimeout();
}
